// Expanded program detail: supported and unsupported options, LTV ratios,
// eligibility exception summary and dialog, product notes and financed MIP.
import Grid from '@elliemae/ds-grid';
import { DSTypography, TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import {
  LoanProgramRowDetails,
  useLoanDataFieldSelectorHook,
} from '@frontend/pricing-search';
import { useEffect, useState } from 'react';
import Session, { IS_DEBUG } from '../../../../common/services/Session';
import { useAppData } from '../../../../common/utils/customHooks/useAppData';
import { dispCurrencyMask } from '../../helpers';
import {
  buildExceptionAdjustmentSummary,
  canManageException,
  deleteEligibilityException,
  EXCEPTION_APPLIED_LABEL,
  ExceptionDialog,
  getEligibilityException,
  isExceptionProgram,
  ManageExceptionButton,
  saveEligibilityException,
  showExceptionAppliedToast,
  showExceptionDeletedToast,
  showExceptionDeleteErrorToast,
  showExceptionErrorToast,
  type EligibilityException,
  type SaveAndQualifyPayload,
} from '../EligibilityException';
import { StyledProductNotesList } from './styled';

const LTV_HELP_URL =
  'https://help.mortgagetech.ice.com/documentation/EPPS-Web/Content/LTVCLTV.htm';

const percent = (val: number) => +(val * 100).toFixed(3);

// Notes arrive with braces standing in for angle brackets and pipes for list breaks.
const parseNotes = (notes: string) =>
  `<ul><li>${(notes ?? '')
    .replace(/{/g, '<')
    .replace(/}/g, '>')
    .replace(/\|/g, '<br/><li>')}</ul>`;

export interface ProgramNotesProps {
  program: LoanProgramRowDetails;
  loanId: number;
}

export const ProgramNotes = ({ program, loanId }: ProgramNotesProps) => {
  const loanData = useLoanDataFieldSelectorHook() as any;
  const qualifyPrograms = useAppData('qualifyPrograms');
  const isDebug = Session.get(IS_DEBUG);
  const showManageException = !isDebug && canManageException(program);
  const hasException = isExceptionProgram(program);

  const [manageExceptionOpen, setManageExceptionOpen] = useState(false);
  // Bumped after a save persists so the cached exception refetches without
  // needing to collapse and re-expand the row.
  const [exceptionRefreshKey, setExceptionRefreshKey] = useState(0);
  const [existingException, setExistingException] =
    useState<EligibilityException | null>(null);
  const [isLoadingException, setIsLoadingException] = useState(false);

  // Persist the edited exception, then re-qualify.
  const handleSaveAndQualify = async ({
    programId,
    body,
  }: SaveAndQualifyPayload): Promise<boolean> => {
    try {
      await saveEligibilityException({ loanId, programId, body });
    } catch {
      showExceptionErrorToast();
      return false;
    }
    setExceptionRefreshKey((key) => key + 1);
    try {
      if (qualifyPrograms) {
        await qualifyPrograms();
      }
    } catch {
      showExceptionErrorToast();
      return true;
    }
    showExceptionAppliedToast(program?.program ?? '');
    return true;
  };

  // Delete the exception then re-qualify. Keep the dialog open only if the
  // delete itself fails; a failed re-qualify still closes (delete persisted).
  const handleDelete = async (): Promise<boolean> => {
    try {
      await deleteEligibilityException({
        loanId,
        programId: program?.programID,
      });
    } catch {
      showExceptionDeleteErrorToast();
      return false;
    }
    try {
      if (qualifyPrograms) {
        await qualifyPrograms();
      }
    } catch {
      showExceptionDeleteErrorToast();
      return true;
    }
    showExceptionDeletedToast(program?.program ?? '');
    return true;
  };

  // Load the saved exception when the program carries one, for the summary line and manage dialog.
  const exceptionProgramId = program?.programID;
  useEffect(() => {
    if (isDebug || !hasException || !loanId || !exceptionProgramId) {
      setExistingException(null);
      return undefined;
    }
    let cancelled = false;
    setIsLoadingException(true);
    getEligibilityException({ loanId, programId: exceptionProgramId })
      .then((result) => {
        if (!cancelled) {
          setExistingException(result);
        }
      })
      .catch(() => {
        // Fall back to no exception rather than leaving a stale one on screen.
        if (!cancelled) {
          setExistingException(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingException(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isDebug, hasException, loanId, exceptionProgramId, exceptionRefreshKey]);

  const showFinancedMIP =
    (program.fha || program.va || program.usda) &&
    loanData?.loanInformation?.feeAmountFinanced > 0;

  return (
    <Grid
      rows="auto"
      gutter="xxs"
      p="xs"
      pr="0"
      data-testid="program-notes-panel"
    >
      {program.options !== '' && (
        <DSTypography variant={TYPOGRAPHY_VARIANTS.B1}>
          <strong>Supported Options: </strong>
          {program.options}
        </DSTypography>
      )}

      {program.nonOptions !== '' && (
        <DSTypography variant={TYPOGRAPHY_VARIANTS.B1}>
          <DSTypography
            as="strong"
            color="danger-900"
            fontWeight={700}
            variant={TYPOGRAPHY_VARIANTS.B1}
          >
            Not Supported Options:{' '}
          </DSTypography>
          {program.nonOptions}
        </DSTypography>
      )}

      <Grid cols={['1fr', 'auto']} alignItems="center" gutter="m">
        <DSTypography variant={TYPOGRAPHY_VARIANTS.B1}>
          <a href={LTV_HELP_URL} target="_blank" rel="noreferrer">
            LTV / CLTV / HCLTV
          </a>
          <strong>
            {'  '}
            {percent(program.ltv)} / {percent(program.cltv)} /{' '}
            {percent(program.hcltv)}
          </strong>
        </DSTypography>
        {showManageException && (
          <ManageExceptionButton onClick={() => setManageExceptionOpen(true)} />
        )}
      </Grid>

      {existingException && (
        <DSTypography
          as="strong"
          color="danger-900"
          fontWeight={700}
          variant={TYPOGRAPHY_VARIANTS.B1}
        >
          {EXCEPTION_APPLIED_LABEL}:{' '}
          {buildExceptionAdjustmentSummary(existingException)}
        </DSTypography>
      )}

      {showManageException && (
        <ExceptionDialog
          isOpen={manageExceptionOpen}
          mode="manage"
          program={program}
          existingException={existingException ?? undefined}
          isLoading={isLoadingException}
          onClose={() => setManageExceptionOpen(false)}
          onDelete={handleDelete}
          onSaveAndQualify={handleSaveAndQualify}
        />
      )}

      <StyledProductNotesList
        data-testid="program-notes"
        dangerouslySetInnerHTML={{ __html: parseNotes(program.notes) }}
      />

      {showFinancedMIP && (
        <DSTypography variant={TYPOGRAPHY_VARIANTS.B1}>
          <DSTypography
            as="strong"
            color="warning-900"
            fontWeight={700}
            variant={TYPOGRAPHY_VARIANTS.B1}
          >
            The financed MIP used for qualification{' '}
          </DSTypography>
          {dispCurrencyMask(loanData.loanInformation.feeAmountFinanced)}
        </DSTypography>
      )}
    </Grid>
  );
};
